import { Injectable, computed, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  FOOTER_PRODUCT_SECTIONS,
  FOOTER_SECURITY_ANCHORS,
  PRODUCT_CONFIG,
  PRODUCT_SECTIONS,
  type ProductConfig,
  type ProductSectionKey,
} from '../data/products.config';
import {
  AGENT_ROUTE,
  APP_URLS,
  COMPANY,
  FOOTER_LINKS,
  CORE_DEMO_HREF,
  DEMO_BOOKING_URL,
  HEADER_PILOT_HREF,
  ONBOARDING,
} from '../data/site.config';
import { canonicalPath, localePath } from '../utils/locale-path';
import { LanguageService } from './language.service';
import { PricingService } from './pricing.service';
import type {
  Cta,
  Faq,
  FaqGroup,
  GuidePage,
  ProductContent,
  ProductSecurityLists,
  ProductSlug,
  ProductSummary,
  WhatsNew,
} from '../models/product.model';
import type {
  CompanyFact,
  CompanyPrinciple,
  FooterColumn,
  HeaderActions,
  NavGroup,
  NavLink,
  SiteContent,
  TrustSignal,
} from '../models/site.model';

/**
 * Everything the site says, in the active language.
 *
 * Config (identity, routes, URLs) comes from `core/data/*.config.ts`; every
 * word comes from `public/i18n/<lang>.json`. This service is the only place
 * the two are joined, so components stay unaware of both.
 *
 * All getters recompute when `LanguageService.lang` changes, which happens
 * only after the new dictionary has loaded.
 */
@Injectable({ providedIn: 'root' })
export class ContentService {
  private readonly translate = inject(TranslateService);
  private readonly language = inject(LanguageService);
  private readonly pricing = inject(PricingService);

  /**
   * Reads a subtree of the active dictionary, typed by the caller.
   *
   * `instant` echoes the key back when a dictionary is missing, so anything
   * that is not the expected shape is treated as absent. Without this a failed
   * fetch would crash the shell rather than render an empty section.
   */
  private tree<T>(key: string, fallback: T): T {
    const value = this.translate.instant(key) as unknown;
    return typeof value === 'object' && value !== null ? (value as T) : fallback;
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params) as string;
  }

  /** Config holds `/catalog`, never `/fr/catalog`; an external address passes through untouched. */
  private href(path: string): string {
    return localePath(path, this.language.lang());
  }

  /** Fetched by index because a tree lookup returns raw strings, with `{{days}}` unfilled. */
  private faqList(base: string, params?: Record<string, unknown>): Faq[] {
    return this.tree<Faq[]>(base, []).map((faq, i) => ({
      ...faq,
      q: this.t(`${base}.${i}.q`, params),
      a: this.t(`${base}.${i}.a`, params),
    }));
  }

  /** Length of the trial the live products offer; copy receives it as `{{days}}`. */
  readonly trialDays = PRODUCT_CONFIG.find((config) => config.trialDays)?.trialDays;

  readonly products = computed<readonly ProductContent[]>(() => {
    this.language.lang();
    return PRODUCT_CONFIG.map((config) => {
      const copy = this.tree<Record<string, unknown>>(`${config.slug}`, {});
      const primary: Cta = {
        id: `${config.slug}_primary`,
        label: copy['ctaPrimary'] as string,
        href: this.href(config.primaryCta.href),
        external: config.primaryCta.external,
        variant: 'primary',
      };
      const secondary: Cta | undefined = config.secondaryCta
        ? {
            id: `${config.slug}_secondary`,
            label: copy['ctaSecondary'] as string,
            href: this.href(config.secondaryCta.href),
            external: config.secondaryCta.external,
            variant: 'secondary',
          }
        : undefined;

      return {
        ...(copy as unknown as Omit<ProductContent, keyof typeof config | 'primaryCta' | 'secondaryCta'>),
        slug: config.slug,
        accent: config.accent,
        stage: config.stage,
        route: this.href(config.route),
        media: config.media,
        contactHref: this.href(config.contactHref),
        overviewScreen: config.overviewScreen,
        pricingKey: config.pricingKey,
        faqs: this.faqList(`${config.slug}.faqs`, { days: config.trialDays }),
        primaryCta: primary,
        secondaryCta: secondary,
        trialLabel: config.trialDays
          ? this.t('common.trialBadge', { days: config.trialDays })
          : undefined,
        logo: config.logo,
      } as ProductContent;
    });
  });

  readonly summaries = computed<readonly ProductSummary[]>(() =>
    this.products().map((p) => ({
      slug: p.slug,
      name: p.name,
      shortName: p.shortName,
      accent: p.accent,
      stage: p.stage,
      stageLabel: p.stageLabel,
      category: p.category,
      oneLiner: p.oneLiner,
      summary: p.summary,
      highlights: p.highlights,
      primaryCta: p.primaryCta,
      secondaryCta: p.secondaryCta,
      trialLabel: p.trialLabel,
      logo: p.logo,
      route: p.route,
      media: p.media,
      contactHref: p.contactHref,
      overviewScreen: p.overviewScreen,
    })),
  );

  readonly liveProducts = computed(() => this.products().filter((p) => p.stage === 'live'));

  readonly site = computed<SiteContent>(() => {
    this.language.lang();

    const agent = this.agentLink();
    const navProducts: NavLink[] = PRODUCT_CONFIG.map((c) => ({
      slug: c.slug,
      label: this.t(`site.nav.products.${c.slug}.label`),
      shortLabel: this.t(`${c.slug}.shortName`),
      href: this.href(c.route),
      description: this.t(`site.nav.products.${c.slug}.description`),
      badge: this.t(`site.nav.products.${c.slug}.badge`),
      accent: c.accent,
      stage: c.stage,
      children: AGENT_ROUTE.startsWith(`${c.route}/`) ? [agent] : undefined,
    }));

    const nav: NavGroup[] = [
      { label: this.t('site.nav.productsLabel'), links: navProducts },
    ];

    return {
      company: COMPANY,
      nav,
      trust: this.tree<TrustSignal[]>('site.trust', []),
      faqs: this.faqList('site.faqs', { days: this.trialDays }),
    };
  });

  /**
   * The bar's three actions: sign in, a walkthrough, and a start.
   *
   * The start is Catalog's free trial everywhere but inside Core's area, where
   * it becomes Core's pilot conversation: Core has no trial, and a bar reading
   * "Start free trial" over a Core page would say it does. The trial's hint
   * names the product, so the label is never a claim about both.
   *
   * The demo follows the same split: the booking page is Catalog's, so inside
   * Core's area the demo is the contact form, preset to a walkthrough.
   */
  headerActions(product: ProductConfig | null): HeaderActions {
    this.language.lang();
    const inCore = product?.slug === 'core';
    const start: Cta = inCore
      ? {
          id: 'header_pilot',
          label: this.t('site.headerCtas.pilot'),
          href: this.href(HEADER_PILOT_HREF),
          variant: 'primary',
        }
      : {
          id: 'header_trial',
          label: this.t('site.headerCtas.trial'),
          href: ONBOARDING.catalog,
          external: true,
          variant: 'primary',
        };
    return {
      login: { label: this.t('site.headerCtas.login'), href: APP_URLS.catalog },
      demo: inCore
        ? {
            id: 'header_demo',
            label: this.t('site.headerCtas.demo'),
            href: this.href(CORE_DEMO_HREF),
            variant: 'secondary',
          }
        : {
            id: 'header_demo',
            label: this.t('site.headerCtas.demo'),
            href: DEMO_BOOKING_URL,
            external: true,
            variant: 'secondary',
          },
      demoHint: inCore ? null : this.t('site.headerCtas.demoHint'),
      start,
      startHint: inCore ? null : this.t('site.headerCtas.trialHint', { days: this.trialDays }),
    };
  }

  readonly companyPrinciples = computed<readonly CompanyPrinciple[]>(() => {
    this.language.lang();
    return this.tree<CompanyPrinciple[]>('home.company.principles.items', []);
  });

  /**
   * Fetched by index for the same reason as `faqList`: `{{currency}}` is filled per row.
   * The currency is the one the prices on screen use, so the two can never disagree.
   */
  readonly companyFacts = computed<readonly CompanyFact[]>(() => {
    this.language.lang();
    const currency = this.pricing.currency();

    return this.tree<CompanyFact[]>('home.company.facts.rows', []).map((fact, i) => ({
      k: fact.k,
      v: this.t(`home.company.facts.rows.${i}.v`, { currency }),
    }));
  });

  /** Part of Core rather than a product: absent from `products()`, carried as that product's nav child. */
  readonly agentLink = computed<NavLink>(() => {
    this.language.lang();
    return {
      label: this.t('site.nav.agent.label'),
      badge: this.t('site.nav.agent.badge'),
      href: this.href(AGENT_ROUTE),
      description: this.t('site.nav.agent.description'),
      accent: 'core',
    };
  });

  bySlug(slug: ProductSlug): ProductContent | undefined {
    return this.products().find((p) => p.slug === slug);
  }

  /** The product's guide pages, in config order, each joined to its copy. */
  guidePages(slug: ProductSlug): readonly GuidePage[] {
    this.language.lang();
    const config = PRODUCT_CONFIG.find((p) => p.slug === slug);
    if (!config) return [];

    const copy = this.tree<Record<string, Partial<GuidePage>>>(`${slug}.guide.pages`, {});
    return [...config.guidePages]
      .sort((a, b) => a.order - b.order)
      .map((page) => ({
        ...page,
        route: this.href(`${config.route}/guide/${page.slug}`),
        title: copy[page.slug]?.title ?? '',
        summary: copy[page.slug]?.summary ?? '',
        sections: copy[page.slug]?.sections ?? [],
      }));
  }

  guidePage(slug: ProductSlug, page: string): GuidePage | undefined {
    return this.guidePages(slug).find((p) => p.slug === page);
  }

  /** Questions are selected by `topicKey`, never by the translated `topic` label. */
  faqs(slug: ProductSlug, topicKey?: string): readonly Faq[] {
    const all = this.bySlug(slug)?.faqs ?? [];
    return topicKey ? all.filter((f) => f.topicKey === topicKey) : all;
  }

  topFaqs(slug: ProductSlug): readonly Faq[] {
    return this.faqs(slug).filter((f) => f.placement === 'overview');
  }

  faqGroups(slug: ProductSlug): readonly FaqGroup[] {
    const byKey = new Map<string, Faq[]>();
    for (const faq of this.faqs(slug)) {
      const key = faq.topicKey ?? '';
      byKey.set(key, [...(byKey.get(key) ?? []), faq]);
    }
    return [...byKey].map(([key, faqs]) => ({ key, label: faqs[0].topic ?? key, faqs }));
  }

  whatsNew(slug: ProductSlug): WhatsNew {
    this.language.lang();
    const copy = this.tree<Partial<WhatsNew>>(`${slug}.whatsNew`, {});
    return { shipped: copy.shipped ?? [], next: copy.next ?? [] };
  }

  security(slug: ProductSlug): ProductSecurityLists {
    this.language.lang();
    const copy = this.tree<{
      consent?: { facts?: ProductSecurityLists['consentFacts'] };
      permissions?: { items?: ProductSecurityLists['permissions'] };
      limits?: { items?: ProductSecurityLists['limits'] };
      payments?: { items?: ProductSecurityLists['payments'] };
      open?: { items?: ProductSecurityLists['open'] };
    }>(`${slug}.security`, {});
    return {
      consentFacts: copy.consent?.facts ?? [],
      permissions: copy.permissions?.items ?? [],
      limits: copy.limits?.items ?? [],
      payments: copy.payments?.items ?? [],
      open: copy.open?.items ?? [],
    };
  }

  footer(slug: ProductSlug | null): readonly FooterColumn[] {
    this.language.lang();
    const product = slug ? this.bySlug(slug) : undefined;
    const config = PRODUCT_CONFIG.find((c) => c.slug === slug);
    return [
      this.footerProductsColumn(),
      ...(product && config ? [this.footerProductColumn(product, config.sections)] : []),
      ...this.footerFixedColumns(),
    ];
  }

  private footerProductsColumn(): FooterColumn {
    const agent = this.agentLink();
    return {
      title: this.t('site.footer.products'),
      links: this.products().map((p) => ({
        label: p.name,
        href: p.route,
        // AGENT_ROUTE has no language prefix, p.route does.
        children: AGENT_ROUTE.startsWith(`${canonicalPath(p.route)}/`)
          ? [{ label: agent.label, href: agent.href }]
          : undefined,
      })),
    };
  }

  private footerProductColumn(
    product: ProductContent,
    pages: readonly ProductSectionKey[],
  ): FooterColumn {
    const sections = PRODUCT_SECTIONS.filter(
      (section) => FOOTER_PRODUCT_SECTIONS.has(section.key) && pages.includes(section.key),
    );
    return {
      title: product.name,
      links: sections.map((section) => {
        const href = `${product.route}/${section.path}`;
        return {
          label: this.t(`site.nav.product.${section.key}`),
          href,
          children:
            section.key === 'security'
              ? FOOTER_SECURITY_ANCHORS.map((anchor) => ({
                  label: this.t(`site.footer.product.${anchor.key}`),
                  href: `${href}#${anchor.fragment}`,
                }))
              : undefined,
        };
      }),
    };
  }

  private footerFixedColumns(): FooterColumn[] {
    const copy = this.tree<{ title: string; links: string[] }[]>('site.footer.columns', []);
    // Labels pair with links by position, so a link whose environment address is empty is
    // dropped after pairing; a column left with no links is dropped too.
    return FOOTER_LINKS.map((column, i) => ({
      title: copy[i]?.title ?? '',
      links: column
        .map((link, j) => ({ link, label: copy[i]?.links[j] ?? '' }))
        .filter(({ link }) => link.href.trim() !== '')
        .map(({ link, label }) => ({
          label,
          href: this.href(link.href),
          external: link.external,
        })),
    })).filter((column) => column.links.length > 0);
  }
}
